import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { useCurrentUser } from 'FrontRoyalAngular';
import { targetBrandConfig } from 'AppBranding';
import { MailtoLink } from 'MailtoLink';
import { Button } from 'Button';
import { Text } from 'Text';
import { type LearnerProject } from 'LearnerProjects';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { type SerializedError } from '@reduxjs/toolkit';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { MembersList } from './MembersList';
import { getMemberDetailsFromProjectGroupMembership } from './getMemberDetails';
import {
    type MemberDetails,
    type ProjectGroupMembershipWithProjectGroup,
    type ProjectGroupMembershipApiResponse,
    MemberDetailsMembershipStatus,
    ProjectGroupMembershipStatus,
} from './ProjectGroup.types';
import { projectGroupMembershipsApi } from './projectGroupMembershipsApi';
import './locales/project_group_invitation_modal-en.json';

const Description = memo(({ className, project }: { className?: string; project: LearnerProject }) => {
    const { t } = useTranslation('back_royal');

    return (
        <div className={className}>
            <Text as="h2" className="mb-7.5 text-center" color="slate-grey" weight="normal" uppercase>
                {project.title}
            </Text>
            <Text className="whitespace-pre-line text-base" color="black" weight="normal">
                {t('project_group_invitation_modal.description')}
            </Text>
        </div>
    );
});

const Buttons = memo(
    ({
        className,
        isLoading,
        onAccept,
        onDecline,
    }: {
        className?: string;
        isLoading: boolean;
        onAccept: () => Promise<void>;
        onDecline: () => Promise<void>;
    }) => {
        const { t } = useTranslation('back_royal');

        return (
            <div className={twMerge('flex flex-col gap-3 xs:flex-row xs:justify-center xs:gap-7.5', className)}>
                <Button className="font-normal" variant="light" onPress={onDecline} isDisabled={isLoading}>
                    {t('project_group_invitation_modal.decline')}
                </Button>
                <Button variant="solid" color="primary" onPress={onAccept} isDisabled={isLoading}>
                    {t('project_group_invitation_modal.accept')}
                </Button>
            </div>
        );
    },
);

const Spinner = memo(
    ({ className, isAccepting, isDeclining }: { className?: string; isAccepting: boolean; isDeclining: boolean }) => {
        if (isAccepting || isDeclining) {
            return <FrontRoyalSpinner className={twMerge('no-top-margin no-delay', className)} />;
        }
        return null;
    },
);

const ErrorMessage = memo(({ className, error }: { className?: string; error: string | null }) => {
    if (error) {
        return <Text className={twMerge('text-center text-red-500', className)}>{error}</Text>;
    }
    return null;
});

const Footnote = memo(() => {
    const { t } = useTranslation('back_royal');
    const currentUser = useCurrentUser();
    const brandConfig = targetBrandConfig(currentUser);
    const projectEmailAddress = brandConfig.emailAddressForUsername('projects');

    return (
        <div className="flex justify-center text-center">
            <Text as="small" color="slate-grey">
                {t('project_group_invitation_modal.footnote', {
                    email: <MailtoLink className="text-xs" email={projectEmailAddress} key="projects-mailto" />,
                })}
            </Text>
        </div>
    );
});

function ProjectGroupInvitationComponent(props: {
    closeModal: () => void;
    projectGroupMembership: ProjectGroupMembershipWithProjectGroup;
}) {
    const { closeModal, projectGroupMembership } = props;
    const { t } = useTranslation('back_royal');

    const invitedAndAcceptedMembers = projectGroupMembership
        // Only include other invited / accepted memberships, i.e. not declined / removed memberships.
        .projectGroup!.memberships!.filter(membership =>
            [ProjectGroupMembershipStatus.INVITED, ProjectGroupMembershipStatus.ACCEPTED].includes(membership.status),
        )
        .map(membership => getMemberDetailsFromProjectGroupMembership(membership))
        // Put the creator first
        .sort((a, b) => {
            const aIsCreator = a.detailsStatus === MemberDetailsMembershipStatus.CREATOR;
            const bIsCreator = b.detailsStatus === MemberDetailsMembershipStatus.CREATOR;

            // Creator comes first
            if (aIsCreator && !bIsCreator) return -1;
            if (!aIsCreator && bIsCreator) return 1;

            // Current user comes next
            if (a.id === projectGroupMembership.userId) return -1;
            if (b.id === projectGroupMembership.userId) return 1;

            return 0; // maintain current order
        });

    const [error, setError] = useState<string | null>(null);
    const [accept, { isLoading: isAccepting }] = projectGroupMembershipsApi.useAcceptMutation();
    const [decline, { isLoading: isDeclining }] = projectGroupMembershipsApi.useDeclineMutation();
    const handleApiResponse = (
        response:
            | {
                  data: ProjectGroupMembershipApiResponse;
                  error?: undefined;
              }
            | {
                  data?: undefined;
                  error: SerializedError | FetchBaseQueryError;
              },
    ) => {
        if (response.error) {
            setError(t('project_group_invitation_modal.api_error'));
            return;
        }
        closeModal();
    };
    const triggerMutation = async (mutation: typeof accept | typeof decline) => {
        if (isAccepting || isDeclining) return;
        setError(null);
        try {
            const response = await mutation({ id: projectGroupMembership.id });
            handleApiResponse(response);
        } catch {
            setError(t('project_group_invitation_modal.api_error'));
        }
    };

    return (
        <>
            <Description className="mb-9" project={projectGroupMembership.projectGroup!.project!} />
            <MembersList
                className={{ base: 'mb-12.5' }}
                members={invitedAndAcceptedMembers}
                showMemberStatus={(member: MemberDetails) =>
                    member.detailsStatus === MemberDetailsMembershipStatus.CREATOR
                }
            />
            <Buttons
                className="mb-3.5"
                isLoading={isAccepting || isDeclining}
                onDecline={() => triggerMutation(decline)}
                onAccept={() => triggerMutation(accept)}
            />
            <Spinner className="mb-3.5" isAccepting={isAccepting} isDeclining={isDeclining} />
            <ErrorMessage className="mb-3.5" error={error} />
            <Footnote />
        </>
    );
}

export const ProjectGroupInvitationPage = memo(
    ProjectGroupInvitationComponent,
) as typeof ProjectGroupInvitationComponent;
